import { useRef, useState, useCallback, useEffect } from "react";
import { getAccessToken, tryRestoreSession } from "@/lib/api";
import { getDeviceKind } from "@/lib/device";

const WS_BASE = import.meta.env.VITE_WS_URL ?? "";

export interface SuggestionItem {
  id: string;
  label: string;
  action: string;
  payload: Record<string, unknown>;
  style?: "primary" | "secondary";
}

export interface DebugSummary {
  type: "debug_summary";
  duration_ms: number;
  lane?: string;
  classifier_skipped?: boolean;
  fast_path_type?: string;
  governor?: {
    lane?: string;
    model?: string;
    hot_limit?: number;
    sections?: string[];
    cold_retrieval?: boolean;
    force_phased?: boolean;
    complexity?: {
      score?: number;
      model?: string;
      phased?: boolean;
      signals?: Record<string, unknown>;
      reasoning?: string;
    };
  };
  classifier?: {
    duration_ms?: number;
    input_tokens?: number;
    output_tokens?: number;
    task_type?: string;
    model?: string;
    tool_groups?: string[];
    complexity?: string;
  };
  agent_loop?: {
    model?: string;
    input_tokens?: number;
    output_tokens?: number;
    tool_calls?: number;
    iterations?: number;
    files_changed?: Array<{ path: string; action: string }>;
  };
  totals?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    tool_calls?: number;
    api_calls?: number;
  };
}

export type ServerMessage =
  | { type: "text_delta"; content: string }
  | { type: "text_done"; content: string }
  | { type: "response_start" }
  | { type: "response_end" }
  | { type: "widget"; widget_type: string; data: Record<string, unknown>; actions?: Array<{ id: string; label: string; style?: string }> }
  | { type: "activity"; description: string }
  | { type: "shell_output"; data: string }
  | { type: "error"; message: string }
  | { type: "agent_status"; connected: boolean; waking?: boolean }
  | { type: "workspace_reset"; reason: string; detail: string }
  | { type: "github_status"; connected: boolean; suspended?: boolean; message: string; reason_code?: "disconnected" | "suspended" | "deleted" }
  | { type: "session_updated"; session_id: string; title: string }
  | { type: "session_rotated"; new_session_id: string }
  | { type: "greeting"; content: string }
  | { type: "route_to_project"; project_id: string; project_name: string; forward_prompt?: string }
  | { type: "context_usage"; percent: number; tokens_used: number; tokens_total: number; compressed?: boolean }
  | { type: "project_renamed"; new_name: string }
  | { type: "route_to_logs" }
  | { type: "log_chunk"; source: string; entries: Array<{ timestamp: string; message: string; source: string; level: string }> }
  | { type: "suggestions"; items: SuggestionItem[] }
  | { type: "github_status"; connected: boolean; installation_id?: string; account_login?: string }
  | { type: "onboarding_start" }
  | { type: "onboarding_end" }
  | { type: "project_deleted"; project_id: string; project_name: string }
  | { type: "task_resume"; session_id: string; is_running: boolean; event_count: number }
  | { type: "turn_complete"; status: "ok" | "error" | "interrupted"; error?: string; replay?: boolean }
  | DebugSummary;

interface UseWebSocketOptions {
  projectId: string;
  onMessage?: (msg: ServerMessage) => void;
}

type ConnectionState = "connecting" | "connected" | "disconnected";

export function useWebSocket({ projectId, onMessage }: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ConnectionState>("disconnected");
  const [agentConnected, setAgentConnected] = useState(false);
  const [agentWaking, setAgentWaking] = useState(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    stoppedRef.current = false;
    const token = getAccessToken();
    if (!token || !projectId) return;

    const base = WS_BASE || window.location.origin;
    const wsUrl = base.replace(/^http/, "ws");
    // No token in URL — sent via first message after connect
    const url = `${wsUrl}/ws/client`;

    setState("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send auth as first message (keeps JWT out of URL/logs)
      ws.send(JSON.stringify({
        type: "auth",
        token,
        project_id: projectId,
      }));
      setState("connected");
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    ws.onmessage = (event) => {
      if (stoppedRef.current) return;
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        if (msg.type === "agent_status") {
          setAgentConnected(msg.connected);
          if (msg.connected) {
            setAgentWaking(false);
          } else if (msg.waking) {
            setAgentWaking(true);
          }
        }
        onMessageRef.current?.(msg);
      } catch (_err) {
        console.error("[ws] Failed to parse message:", event.data);
      }
    };

    ws.onclose = (event) => {
      setState("disconnected");
      wsRef.current = null;

      // Don't reconnect after intentional disconnect
      if (stoppedRef.current) return;

      // 4003 = auth expired — refresh token, then reconnect
      if (event.code === 4003 || event.code === 403) {
        tryRestoreSession().then((restored) => {
          if (restored && !stoppedRef.current) {
            reconnectTimer.current = setTimeout(connect, 500);
          }
        });
        return;
      }

      // 4001 = auth rejected — try refresh once (token may have expired
      // between page load and WebSocket connect), then give up
      if (event.code === 4001) {
        tryRestoreSession().then((restored) => {
          if (restored && !stoppedRef.current) {
            reconnectTimer.current = setTimeout(connect, 500);
          }
        });
        return;
      }

      // Other close codes — reconnect after delay
      reconnectTimer.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => {
      // onclose will fire after this
    };
  }, [projectId]);

  const disconnect = useCallback(() => {
    stoppedRef.current = true;
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setState("disconnected");
  }, []);

  const send = useCallback((msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const sendChat = useCallback((content: string, sessionId: string) => {
    send({
      type: "chat",
      content,
      session_id: sessionId,
      device: getDeviceKind(),
    });
  }, [send]);

  const sendAction = useCallback((action: string, payload: Record<string, unknown>) => {
    send({ type: "action", action, payload, device: getDeviceKind() });
  }, [send]);

  // When the page resumes from background (common on mobile), the WebSocket
  // is almost certainly dead — even if readyState still says OPEN, the TCP
  // connection was killed by the OS while the page was frozen. Force a full
  // reconnect cycle so the ChatShell recovery can detect the state transition.
  useEffect(() => {
    let hiddenAt = 0;
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== "visible" || stoppedRef.current) return;

      // Only force-reconnect if the page was hidden for >3s (avoids tab-switch flicker)
      if (hiddenAt && Date.now() - hiddenAt > 3000) {
        const ws = wsRef.current;
        if (ws) {
          ws.onclose = null; // prevent the old onclose from scheduling a competing reconnect
          ws.close();
        }
        wsRef.current = null;
        setState("disconnected");
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
        // Small delay so ChatShell sees the disconnected→connected transition
        reconnectTimer.current = setTimeout(connect, 100);
      }
      hiddenAt = 0;
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [connect]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    state,
    agentConnected,
    agentWaking,
    connect,
    disconnect,
    send,
    sendChat,
    sendAction,
  };
}
